/* Product card behavior. Styles are defined in assets/base.css. */
if (!customElements.get('product-item')) {
  class ProductItem extends HTMLElement {
    static root() { return window.Shopify?.routes?.root || '/'; }
    static async json(url, signal) {
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Unable to load product (${response.status})`);
      return response.json();
    }
    static async resolveProduct(value, signal) {
      if (!/^\d+$/.test(value)) {
        const product = await this.json(`${this.root()}products/${encodeURIComponent(value)}.js`, signal);
        return { product, variant: product.variants?.[0] };
      }
      for (let page = 1; ; page++) {
        const { products = [] } = await this.json(`${this.root()}products.json?limit=250&page=${page}`, signal);
        for (const product of products) {
          const variant = product.variants?.find(item => String(item.id) === value);
          if (variant) return { product, variant };
        }
        if (products.length < 250) throw new Error('Product variant not found');
      }
    }
    static get observedAttributes() { return ['handle', 'product-handle', 'button-label']; }
    constructor() {
      super();
      this.index = 0;
    }
    connectedCallback() { this.load(); }
    disconnectedCallback() { this.controller?.abort(); }
    attributeChangedCallback() { if (this.isConnected) this.load(); }
    async load() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.replaceChildren();
      this.setAttribute('aria-busy', 'true');
      const status = document.createElement('p');
      status.setAttribute('role', 'status');
      status.textContent = 'Loading product…';
      this.append(status);
      try {
        const value = this.getAttribute('handle')?.trim();
        if (!value) throw new Error('A product handle or variant ID is required');
        const direct = this.getAttribute('product-handle');
        const result = await ProductItem.resolveProduct(direct || value, signal);
        if (direct && /^\d+$/.test(value)) {
          result.variant = result.product.variants?.find(v => String(v.id) === value);
          if (!result.variant) throw new Error('Product variant not found');
        }
        let features = '';
        // Liquid view supplies metafields unavailable in the product JSON endpoint.
        try {
          const response = await fetch(`${ProductItem.root()}products/${encodeURIComponent(result.product.handle)}?view=product-item`, { signal });
          if (response.ok) {
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            features = doc.querySelector('[data-product-item-features]')?.innerHTML || '';
          }
        } catch (error) { if (error.name === 'AbortError') throw error; }
        if (!signal.aborted) this.render(result, features);
      } catch (error) {
        if (signal.aborted) return;
        status.textContent = 'Product unavailable. Check the handle or variant ID.';
        console.warn('[product-item]', error.message);
      } finally { if (!signal.aborted) this.removeAttribute('aria-busy'); }
    }
    render({ product, variant }, features) {
      this.innerHTML = `<article class="product-item__card"><div class="product-item__gallery"><div class="product-item__stage"><img class="product-item__image"><button class="product-item__arrow product-item__prev" aria-label="Previous product image">&#8249;</button><button class="product-item__arrow product-item__next" aria-label="Next product image">&#8250;</button></div><div class="product-item__dots" aria-label="Product images"></div><span class="product-item__count" role="status"></span></div><div class="product-item__info"><h2 class="product-item__title"></h2><div class="product-item__features"></div><div class="product-item__spacer"></div><a class="product-item__cta"></a></div></article>`;
      const root = this;
      root.querySelector('.product-item__title').textContent = product.title;
      const featureBox = root.querySelector('.product-item__features');
      const doc = new DOMParser().parseFromString(features || product.body_html || product.description || '', 'text/html');
      // Render text-only bullets; never execute product description HTML.
      const lines = [...doc.querySelectorAll('li')].map(e => e.textContent.trim()).filter(Boolean).slice(0, 5);
      if (lines.length) {
        const list = document.createElement('ul');
        lines.forEach(text => { const li = document.createElement('li'); li.textContent = text; list.append(li); });
        featureBox.append(list);
      } else {
        const text = doc.body.textContent.trim();
        const p = document.createElement('p'); p.textContent = text.length > 360 ? `${text.slice(0, 357)}…` : text;
        featureBox.append(p); featureBox.hidden = !text;
      }
      const link = root.querySelector('.product-item__cta');
      link.textContent = this.getAttribute('button-label') || 'Learn More';
      link.href = `${ProductItem.root()}products/${encodeURIComponent(product.handle)}${variant ? `?variant=${encodeURIComponent(variant.id)}` : ''}`;
      this.images = (product.images || []).map(image => typeof image === 'string' ? { src: image } : image).filter(image => image.src);
      const selected = variant?.featured_image?.src || this.images.find(image => String(image.id) === String(variant?.image_id))?.src;
      const selectedIndex = this.images.findIndex(image => image.src === selected);
      this.index = selectedIndex >= 0 ? selectedIndex : 0;
      this.images.forEach((_, i) => {
        const dot = document.createElement('button'); dot.className = 'product-item__dot';
        dot.setAttribute('aria-label', `Show product image ${i + 1}`);
        dot.addEventListener('click', () => this.showImage(i)); root.querySelector('.product-item__dots').append(dot);
      });
      root.querySelector('.product-item__prev').addEventListener('click', () => this.showImage(this.index - 1));
      root.querySelector('.product-item__next').addEventListener('click', () => this.showImage(this.index + 1));
      root.querySelector('.product-item__gallery').addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.showImage(this.index + (event.key === 'ArrowLeft' ? -1 : 1)); }
      });
      for (const e of root.querySelectorAll('.product-item__arrow,.product-item__dots')) e.hidden = this.images.length < 2;
      this.showImage(this.index);
      if (!this.images.length) root.querySelector('.product-item__stage').textContent = 'No product image available';
      this.productTitle = product.title;
      const img = root.querySelector('.product-item__image'); if (img) img.alt = product.title;
    }
    showImage(index) {
      if (!this.images?.length) return;
      this.index = (index + this.images.length) % this.images.length;
      const image = this.images[this.index], img = this.querySelector('.product-item__image');
      img.src = image.src; img.alt = image.alt || this.productTitle || 'Product image';
      this.querySelectorAll('.product-item__dot').forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      this.querySelector('.product-item__count').textContent = `Image ${this.index + 1} of ${this.images.length}`;
    }
  }
  customElements.define('product-item', ProductItem);
}
